/**
 * @file schema.ts
 * @description Payload секции v2 (phase19.16, релиз Р2 «Секции и заготовки»;
 * db-design-r2 §«Payload секции v2», uiux-design-r2 §2, §4; правила SB21–SB26,
 * SB64, SB97, SB100).
 *
 * Секция — единственный тип блока страницы (SB22, решение пользователя
 * 2026-10-09: прежние 14 типов убираются). Состав:
 *   header  — шапка раздела над колонками (надпись, заголовок, значок, линия,
 *             ссылка справа); отдельного элемента «шапка» нет;
 *   columns — 1–4 колонки: пропорции, расстояния, выравнивание по вертикали,
 *             порядок на телефоне (только у двух колонок), до 10 элементов в
 *             колонке (SB64);
 *   look    — оформление: фон (роль цвета / фото / прозрачный), ширина,
 *             «в карточке», высота, до 3 рисунков, отступы ступенями (SB100),
 *             видимость по устройствам.
 * Имя `look`, а не `appearance`: колонка `SitePageBlock.appearance` уже хранит
 * эффекты появления, общие для всех блоков.
 *
 * Схема строгая (лишний ключ — ошибка): она проверяет то, что пишет кабинет.
 * Сохранённый payload читается через `readSectionPayload` (read.ts) — он не
 * падает, а чинит и сообщает, что заменил. Цвета — только роли (SB23), фото,
 * видео и звук — только id медиатеки магазина (SB97; принадлежность проверяет
 * сервер). Ссылки на сущности (`Target`) — без FK: «сирота» на сайте скрывается.
 * Чайберийских значений здесь нет (§7.0, SB27).
 *
 * Р2а «Каталог и товар» (db-design-r2a, ответ 2б): добавлены элементы данных
 * товара (`PRODUCT_ELEMENT_KINDS`) — та же схема и payloadVersion 2, но
 * разрешены они только в шаблоне страницы товара (contexts.ts); у обязательных
 * (`PRODUCT_TEMPLATE_REQUIRED`, SB34) поле `hide` с true — ошибка.
 *
 * Приёмка Р2а (решения пользователя 2026-10-11, §7.17 п.1): обязательных
 * три — «Продавец» выведен (SB36); у «Сведений о товаре» — переключатели
 * строк `rows` (`PRODUCT_FACT_ROWS`, SB35), настройка «состав плашкой»
 * (`highlightComposition`) снята. Новое поле в старых данных — умолчание
 * при чтении, снятое — лишний ключ, его снимает чтение (read.ts);
 * payloadVersion не меняется.
 *
 * v3 — SB120 этап 1 «секция-герой» (db-design-sb120-stage1 §1, решение
 * пользователя 2026-10-18): оформление числами вместо ступеней. Отступы,
 * расстояния, наезд и выход фото за край — px; ширина — px или «Во всю»;
 * высота — по содержимому / по фото / числом / экран (отдельно для
 * телефона); положение содержимого в блоке; доли колонок из 12 и порядок на
 * телефоне для 1–4 колонок; кадр фото для телефона (фокус, масштаб, своё
 * фото); рисунки — точкой x/y в % и размером в px. Правило наследования
 * одно: «как у сайта», «как на ПК», «как у колонки», «Авто» — это **нет
 * ключа** (`.optional()`); `null` — только «явно нет» (`mediaId`,
 * `decor.column`). Различитель новых объединений — `kind` (его понимает
 * `mergeMissingKeys`). Пределы — look-limits.ts. Старые v2 поднимаются
 * `upgradeV2` (upgrade-v3.ts) с плотностью магазина.
 *
 * v4 — SB120 этап 3а «элементы» (db-design-sb120-stage3 §1–§2, решения
 * пользователя 2026-10-18): цвет и шрифт элемента по частям — `typo`
 * (element-typo.ts; `colorRole`, `fontRole` и размер заголовка ступенью
 * сняты), отступ элемента — px (плюс своё на телефоне), стиль надписи —
 * `preset` (ключ `font` теперь у части `typo`), место надписи на фото —
 * x/y % и плашка, у кнопки — заливка, рамка и высота, у галереи на
 * телефоне — «стопкой / по две / лентой», у плиток и категорий — пропорция
 * числом, у шапки раздела — `typo` по частям. Старые v3 поднимаются
 * `upgradeV3` (upgrade-v4.ts) с плотностью и шкалой заголовков темы.
 * Константы v3 — legacy-v3.ts.
 *
 * Этап 3б — добавочно, без новой версии, подъёма и моста (db-design-sb120-
 * stage3 §1): «в ряд» 1–6 у сеток и 1–8 у плиток и категорий, «стопкой» у
 * товаров и «по две» у объявлений, смена объявлений 2–30 с, фото товара
 * «справа»; новые необязательные ключи — `reviews.perRow`, `articles.perRow`
 * / `phoneLayout`, `tiles.shade`, `faq.divider` / `icon`, `table.head` /
 * `stripe` / `lines`, `typo` у категорий, статей, отзывов, объявлений и
 * рассылки. Нет ключа — как было; наборы и помощники — element-grid.ts.
 */

import { z } from "zod";

import { siteColorRefSchema } from "../site-theme/color-ref.js";
import { SECTION_CONTACT_KIND_KEYS } from "../site-theme/contacts-derived.js";
import type { SiteColorRole } from "../site-theme/keys.js";
import { SITE_LIMITS } from "../site-theme/limits.js";
import { siteFillColorSchema, siteSurfaceFillSchema } from "../site-theme/surface-fill.js";
import { isSafeMenuUrl, SITE_SYSTEM_ROUTES } from "../site-theme/menu.js";
import { siteTextColorSchema } from "../site-theme/text-color.js";
import {
  FAQ_ICONS,
  GRID_PER_ROW,
  PHONE_GRID_LAYOUTS,
  PRODUCT_GALLERY_LAYOUTS,
  REVIEW_PER_ROW,
  TILE_GRID_COLS,
} from "./element-grid.js";
import { HEADER_TYPO_PART_KEYS, OVERLAY_TYPO_PART_KEYS, typoFor, typoSchemaOf } from "./element-typo.js";
import { SECTION_LOOK_LIMITS } from "./look-limits.js";

/**
 * Версия payload секции (`SitePageBlock.payloadVersion`). v1 — phase19.8.5–19.8.6,
 * v2 — Р2 (ступени), v3 — SB120 этап 1 (числа), v4 — SB120 этап 3а (элементы).
 */
export const SECTION_PAYLOAD_VERSION = 4;

// ── Справочники ──

/**
 * SB100: шкала отступов сайта — 0, XS, S, M, L, XL. С v3 в схеме секции не
 * хранится: ступени — подсказки-presets ползунков и вход перевода v2 → v3
 * (legacy-steps.ts `spaceStepPx`). Отступы элемента пока ступенями (этап 3).
 */
export const SPACE_STEPS = ["0", "xs", "s", "m", "l", "xl"] as const;
export type SpaceStep = (typeof SPACE_STEPS)[number];

/**
 * Отступ секции v2: ступень или «как у сайта».
 * @deprecated С v3 отступы — числа (`sectionSpaceSchema`); остаётся для перевода v2 → v3.
 */
export const SECTION_SPACE_VALUES = [...SPACE_STEPS, "site"] as const;
export type SectionSpaceValue = (typeof SECTION_SPACE_VALUES)[number];

/** Значки кнопок и шапки раздела (uiux-design-r2 §4: стрелка, Telegram, VK, телефон, корзина, письмо). */
export const SITE_ICON_KEYS = ["arrow", "telegram", "vk", "phone", "cart", "mail"] as const;
export type SiteIconKey = (typeof SITE_ICON_KEYS)[number];

/**
 * Роли цвета, которые годятся в фон секции и колонки (uiux-design-r2 §2:
 * «Как страница», «Карточка», «Плашка», «Тёмный», «Акцент»). Роли текста
 * фоном не бывают — контраст текста подбирает платформа.
 */
export const SECTION_BG_ROLES = [
  "pageBg",
  "cardBg",
  "panelBg",
  "darkBg",
  "accent",
] as const satisfies readonly SiteColorRole[];
export type SectionBgRole = (typeof SECTION_BG_ROLES)[number];

/**
 * Пропорции колонок v2. С v3 в схеме — доли из 12 (`columns.shares`);
 * пропорции остаются быстрыми схемами кабинета и входом перевода v2 → v3
 * (`SECTION_RATIO_SHARES`).
 */
export const SECTION_RATIOS = [
  "1",
  "1:1",
  "2:1",
  "1:2",
  "1:1:1",
  "2:1:1",
  "1:2:1",
  "1:1:2",
  "1:1:1:1",
] as const;
export type SectionRatio = (typeof SECTION_RATIOS)[number];

/**
 * Доли из 12 по пропорции (uiux-sb120-stage1 §5): fr те же, что у v2
 * (1:1 → 6+6, 2:1 → 8+4, 2:1:1 → 6·3·3 …).
 */
export const SECTION_RATIO_SHARES: Readonly<Record<SectionRatio, readonly number[]>> = {
  "1": [12],
  "1:1": [6, 6],
  "2:1": [8, 4],
  "1:2": [4, 8],
  "1:1:1": [4, 4, 4],
  "2:1:1": [6, 3, 3],
  "1:2:1": [3, 6, 3],
  "1:1:2": [3, 3, 6],
  "1:1:1:1": [3, 3, 3, 3],
};

/** Равные доли из 12 для 1–4 колонок: [12], [6,6], [4,4,4], [3,3,3,3]. */
export function equalShares(columns: number): number[] {
  const n = Math.min(4, Math.max(1, Math.trunc(columns)));
  return Array.from({ length: n }, () => SECTION_LOOK_LIMITS.shares.total / n);
}

/** Доли колонок верны: по одной на колонку, целые 1–12, сумма 12. */
export function isValidShares(shares: readonly number[], columns: number): boolean {
  const { min, max, total } = SECTION_LOOK_LIMITS.shares;
  return (
    shares.length === columns &&
    shares.every((v) => Number.isInteger(v) && v >= min && v <= max) &&
    shares.reduce((sum, v) => sum + v, 0) === total
  );
}

/** Порядок на телефоне верен: перестановка 1..N (каждая колонка ровно один раз). */
export function isValidPhoneOrder(order: readonly number[], columns: number): boolean {
  if (order.length !== columns) return false;
  const sorted = [...order].sort((a, b) => a - b);
  return sorted.every((v, i) => v === i + 1);
}

/**
 * Число колонок, которое описывает пропорция («2:1» → 2).
 * @deprecated v3 хранит доли (`columns.shares`); для быстрых схем кабинета.
 */
export function ratioColumnCount(ratio: SectionRatio): number {
  return ratio.split(":").length;
}

/**
 * Равные пропорции для числа колонок.
 * @deprecated v3 — `equalShares`; остаётся для подъёма v1 → v2.
 */
export function equalRatio(columns: number): SectionRatio {
  const n = Math.min(4, Math.max(1, Math.trunc(columns)));
  return (n === 1 ? "1" : Array.from({ length: n }, () => "1").join(":")) as SectionRatio;
}

/** Ручные бейджи товара (enum `ManualBadge`; SB32 — «Новинка» только автоматически, источник `new`). */
export const SECTION_PRODUCT_BADGES = ["BESTSELLER", "COMING_SOON", "SALE"] as const;
export type SectionProductBadge = (typeof SECTION_PRODUCT_BADGES)[number];

/** Виды элементов секции v2 в порядке показа в меню «⊕ элемент». */
export const SECTION_ELEMENT_KINDS_V2 = [
  "heading",
  "eyebrow",
  "text",
  "image",
  "gallery",
  "video",
  "audio",
  "button",
  "tiles",
  "products",
  "categories",
  "articles",
  "reviews",
  "faq",
  "contacts",
  "newsletter",
  "announcements",
  "stats",
  "table",
] as const;
/** Общие виды элементов (обычные страницы, места, шаблон товара). */
export type GeneralElementKind = (typeof SECTION_ELEMENT_KINDS_V2)[number];

/**
 * Р2а (db-design-r2a, ответ 2б; uiux-design-r2a §3): элементы данных товара —
 * часть страницы товара, разрезанной на элементы. Живут в той же схеме
 * секции (один формат, один рендер), но разрешены только в контексте
 * `productTemplate` (contexts.ts). Данные берутся из товара; у элемента —
 * только вид. Порядок — порядок группы «Данные товара» в меню «⊕ элемент».
 */
export const PRODUCT_ELEMENT_KINDS = [
  "product-gallery",
  "product-title",
  "product-buy",
  "product-badges",
  "product-short",
  "product-description",
  "product-facts",
  "product-specs",
  "product-tags",
  "product-reviews",
  "product-related",
  "breadcrumbs",
  "product-seller",
] as const;
export type ProductElementKind = (typeof PRODUCT_ELEMENT_KINDS)[number];

/**
 * SB34: обязательные элементы шаблона страницы товара — название (H1), цена
 * и покупка, сведения о товаре. Удалить и скрыть нельзя, переставить можно.
 * «Продавец» выведен из обязательных при приёмке Р2а (решение пользователя
 * 2026-10-11, SB36): на этапе 1 реквизиты магазина уже в юридической строке
 * подвала (SB19) на каждой странице. Этап 2 (мастер ≠ лицо в подвале) вернёт
 * его в обязательные — правкой этой константы (остальное считается от неё).
 */
export const PRODUCT_TEMPLATE_REQUIRED = [
  "product-title",
  "product-buy",
  "product-facts",
] as const satisfies readonly ProductElementKind[];
export type ProductRequiredKind = (typeof PRODUCT_TEMPLATE_REQUIRED)[number];

/** Все виды элементов секции: общие + данные товара. */
export const SECTION_ELEMENT_KINDS_ALL = [...SECTION_ELEMENT_KINDS_V2, ...PRODUCT_ELEMENT_KINDS] as const;
export type SectionElementKind = (typeof SECTION_ELEMENT_KINDS_ALL)[number];

/** Длины текстов (знаков). Одни числа для схемы и счётчиков в кабинете («11/40»). */
export const SECTION_TEXT_LIMITS = {
  heading: 160,
  text: 4000,
  /** Текст кнопки и ссылки шапки. */
  label: 40,
  eyebrow: 80,
  overlay: 120,
  faqQuestion: 200,
  faqAnswer: 2000,
  alt: 300,
  audioTitle: 200,
  tileTitle: 80,
  tileText: 200,
  url: 500,
  elementId: 40,
  /** Текст одного объявления в «Строке объявлений». */
  announcement: 160,
  /** «Цифры»: значение («10 лет», «500+») и подпись. */
  statValue: 16,
  statLabel: 80,
  /** «Таблица»: заголовок столбца и ячейка. */
  tableTitle: 60,
  tableCell: 200,
} as const;

const T = SECTION_TEXT_LIMITS;

/**
 * Адрес цели «Ссылка»: путь сайта `/…` или `https://…` (как у меню) плюс
 * `tel:` и `mailto:` — для кнопок «Позвонить» и «Написать».
 */
export function isSafeSectionUrl(url: string): boolean {
  if (/^(?:tel|mailto):[^\s<>"'`]+$/i.test(url)) return url.length <= T.url;
  return url.length <= T.url && isSafeMenuUrl(url);
}

// ── Кирпичи ──

/** id `ContentMediaAsset` магазина; принадлежность и тип проверяет сервер. */
const mediaId = z.string().min(1).max(64);
/** id сущности магазина (страница, категория, товар, статья, отзыв). */
const entityId = z.string().min(1).max(64);

/** Текст до `max` знаков; пустой допустим — на сайте не показывается (SB21). */
const optionalText = (max: number) => z.string().max(max, `Не длиннее ${max} знаков`);

/** Обязательный текст: не пустой и не из одних пробелов. */
const requiredText = (max: number, empty: string) =>
  z
    .string()
    .max(max, `Не длиннее ${max} знаков`)
    .refine((value) => value.trim().length > 0, empty);

/** Точка фокуса фото в процентах от левого верхнего угла. */
export const sectionFocusSchema = z.strictObject({
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100),
});

const LIM = SECTION_LOOK_LIMITS;

/** Пиксели 0…max; дробные разрешены (30.72 px после перевода ступеней при «Компактно»). */
const px = (max: number) => z.number().finite().min(0).max(max);
/** Число в пределах {min, max}. */
const ranged = (range: { readonly min: number; readonly max: number }) => z.number().finite().min(range.min).max(range.max);
/** Пропорции кадра (ширина / высота). */
const aspectSchema = ranged(LIM.aspect);

const deviceFlags = z.strictObject({
  phone: z.boolean(),
  tablet: z.boolean(),
  desktop: z.boolean(),
});

/**
 * Куда ведёт ссылка (кнопка, плитка, ссылка шапки). Без FK: цель удалена —
 * элемент на сайте скрывается, в кабинете помечается (как пункт меню, SB16).
 */
export const sectionTargetSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("page"), id: entityId }),
  z.strictObject({ type: z.literal("category"), id: entityId }),
  z.strictObject({ type: z.literal("content-category"), id: entityId }),
  z.strictObject({ type: z.literal("legal"), id: entityId }),
  z.strictObject({ type: z.literal("system"), route: z.enum(SITE_SYSTEM_ROUTES) }),
  z.strictObject({
    type: z.literal("url"),
    url: z
      .string()
      .min(1, "Укажите адрес")
      .refine(isSafeSectionUrl, "Адрес — путь сайта «/…», https://…, tel: или mailto:"),
  }),
]);
export type SectionTarget = z.infer<typeof sectionTargetSchema>;

// ── Элементы (Э1–Э7, SB24–SB26) ──

/** Поля, общие для всех элементов. */
const elementBase = {
  /** Уникален в пределах блока; кабинет и канва адресуют элемент по нему. */
  id: z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/, "id элемента — латиница, цифры, «-» и «_»")
    .min(1)
    .max(T.elementId),
  /** Скрыть на устройствах (true — скрыть); нет ключа — виден везде. */
  hide: deviceFlags.optional(),
  /**
   * Отступ сверху/снизу, px — исключение из ритма колонки (v4, SB120 этап 3а).
   * Нет ключа — ритм колонки (`elementGap`); телефон — ПК × доля темы
   * (`--site-space-mobile`), «своё» — `topPhone` / `bottomPhone`.
   */
  space: z
    .strictObject({
      top: px(LIM.elementSpace.max).optional(),
      bottom: px(LIM.elementSpace.max).optional(),
      topPhone: px(LIM.elementSpace.max).optional(),
      bottomPhone: px(LIM.elementSpace.max).optional(),
    })
    .optional(),
  /** Выравнивание в колонке; нет ключа — как у колонки. */
  align: z.enum(["left", "center", "right"]).optional(),
};

const headingLevel = z.union([z.literal(1), z.literal(2), z.literal(3)]);
/**
 * Прежний переключатель «В ряд на ПК» у карточек (товары, объявления,
 * галерея, цифры, похожие товары): 2–4. С этапа 3б схема принимает 1–6
 * (`GRID_PER_ROW`, element-grid.ts); набор остаётся вариантами кабинета,
 * пока панель не перешла на число.
 */
export const CARD_COLS = [2, 3, 4] as const;
/**
 * Прежний переключатель «В ряд на ПК» у плиток и категорий: 2–6 (SB24,
 * дополнение 2026-10-11). С этапа 3б схема принимает 1–8
 * (`TILE_GRID_COLS`); подсказки числа — `TILE_COLS_HINTS`.
 */
export const TILE_COLS = [2, 3, 4, 5, 6] as const;

// Один источник: значения берём из наборов element-grid.ts (zod 4 принимает массив в literal).
// SB120 этап 3б — добавочно: расширены пределы, старые значения 2–4 / 2–6 проходят как были.
const perRow = z.literal(GRID_PER_ROW);
const perRowWide = z.literal(TILE_GRID_COLS);
const reviewsPerRow = z.literal(REVIEW_PER_ROW);

/**
 * Линия элемента (этап 3б): толщина — целые px в пределах, цвет — роль
 * палитры или свой; нет `color` — «Авто: линия» (`--sec-line`).
 */
const elementLineSchema = (range: { readonly min: number; readonly max: number }) =>
  z.strictObject({
    width: z.number().int().min(range.min).max(range.max),
    color: siteColorRefSchema.optional(),
  });

/** Разделитель вопросов: 0–4 px; нет `divider` — 1 px цветом линии (как было). */
export const sectionFaqDividerSchema = elementLineSchema(LIM.faqDivider);
export type SectionFaqDivider = z.infer<typeof sectionFaqDividerSchema>;

/** Линии таблицы: 0–4 px; нет `lines` — 1 px между строками и 2 px под шапкой (как было). */
export const sectionTableLinesSchema = elementLineSchema(LIM.tableLines);
export type SectionTableLines = z.infer<typeof sectionTableLinesSchema>;

/**
 * Стиль надписи («Надпись» и надписи на фото) — основа, поверх которой
 * `typo`: caps — шрифтом надписей капсом (700, 14 px, разрядка темы),
 * script — шрифтом акцента (24 px, цвет заголовка). Значение `script` — от
 * прежнего «Рукописная», в данных не меняется (SB10, 2026-10-11). До v4
 * ключ назывался `font`; с v4 `font` — шрифт части `typo` (SB120 этап 3а).
 */
export const CAPTION_PRESETS = ["caps", "script"] as const;
export type CaptionPreset = (typeof CAPTION_PRESETS)[number];

/** Подписи стилей надписи — одни на обе панели кабинета. */
export const CAPTION_PRESET_LABELS: Readonly<Record<CaptionPreset, string>> = {
  caps: "Капсом",
  script: "Акцентом",
};

/**
 * Плашка под надписью на фото (v4): заливка — роль или свой цвет, плотность
 * 0–100 %. Нет `plate` — чёрная 60 % (как было); нет `fill` — чёрная;
 * `density: 0` — «Нет»; «Светлая» — свой #FFFFFF (решает кабинет).
 */
export const sectionOverlayPlateSchema = z.strictObject({
  fill: siteColorRefSchema.optional(),
  density: ranged(LIM.overlayPlate).optional(),
});
export type SectionOverlayPlate = z.infer<typeof sectionOverlayPlateSchema>;

/**
 * Надпись поверх фото (v4). Место — x/y % внутри кадра без 12 px от края,
 * со сдвигом −x/−y % и выравниванием текста по x (< 50 слева, 50 по
 * центру, > 50 справа) — для {0, 50, 100} это прежние 9 точек 3×3.
 */
export const sectionOverlaySchema = z.strictObject({
  text: requiredText(T.overlay, "Впишите текст надписи"),
  preset: z.enum(CAPTION_PRESETS),
  x: ranged(LIM.decorPosition),
  y: ranged(LIM.decorPosition),
  plate: sectionOverlayPlateSchema.optional(),
  typo: typoSchemaOf(OVERLAY_TYPO_PART_KEYS),
  hideOnPhone: z.boolean(),
});
export type SectionOverlay = z.infer<typeof sectionOverlaySchema>;

/**
 * Заголовок: уровень H1–H3 — смысл для поиска и чтения с экрана; размер —
 * `typo.main.size` / `sizePhone` px (нет ключа — размер уровня × шкала
 * заголовков темы, как было).
 */
const headingElement = z.strictObject({
  ...elementBase,
  kind: z.literal("heading"),
  level: headingLevel,
  text: optionalText(T.heading),
  typo: typoFor("heading"),
});

const eyebrowElement = z.strictObject({
  ...elementBase,
  kind: z.literal("eyebrow"),
  text: optionalText(T.eyebrow),
  /** Основа: caps — «Капсом», script — «Акцентом»; `typo` — поверх основы. */
  preset: z.enum(CAPTION_PRESETS),
  typo: typoFor("eyebrow"),
});

const textElement = z.strictObject({
  ...elementBase,
  kind: z.literal("text"),
  /** Абзацы — через пустую строку. */
  text: optionalText(T.text),
  typo: typoFor("text"),
});

/** Режимы фото в колонке: обычное и вырезанный объект за край. */
export const SECTION_IMAGE_MODES = ["fit", "bleed"] as const;
export type SectionImageMode = (typeof SECTION_IMAGE_MODES)[number];

/**
 * Высота фото в колонке (uiux-sb120-stage1 §3 и «Дополнение»): photo — по
 * пропорциям (файла или `aspect`), фото целиком; column — тянется на высоту
 * ряда, не меньше `min` (нет ключа — 256 px, прежнее «Заполнить колонку»);
 * number — px, на телефоне `valuePhone` (нет ключа — ПК × доля темы).
 */
export const sectionImageHeightSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("photo") }),
  z.strictObject({ kind: z.literal("column"), min: px(LIM.imageHeight.max).optional() }),
  z.strictObject({
    kind: z.literal("number"),
    value: px(LIM.imageHeight.max),
    valuePhone: px(LIM.imageHeight.max).optional(),
  }),
]);
export type SectionImageHeight = z.infer<typeof sectionImageHeightSchema>;

const imageElement = z.strictObject({
  ...elementBase,
  kind: z.literal("image"),
  /** null — «место под фото» из заготовки; на сайте не показывается (SB21). */
  mediaId: mediaId.nullable(),
  alt: optionalText(T.alt).optional(),
  /** fit — обычное, bleed — вырезанный объект за край (v2 «fill» — это fit + высота «По колонке»). */
  mode: z.enum(SECTION_IMAGE_MODES),
  /** Высота фото: по фото (пропорции), по колонке, числом (SB120 этап 1, uiux §3). */
  height: sectionImageHeightSchema,
  /** Пропорции кадра (ширина / высота); нет ключа — как у файла («По фото»). */
  aspect: aspectSchema.optional(),
  /** Пропорции на телефоне; нет ключа — как на ПК. */
  aspectPhone: aspectSchema.optional(),
  focus: sectionFocusSchema.optional(),
  /** Точка фокуса на телефоне; нет ключа — как на ПК. */
  focusPhone: sectionFocusSchema.optional(),
  overlays: z
    .array(sectionOverlaySchema)
    .max(SITE_LIMITS.sectionOverlays, `На фото не больше ${SITE_LIMITS.sectionOverlays} надписей`),
});

/** Галерея на телефоне: стопкой (одна колонка), по две, лентой — алиас `PHONE_GRID_LAYOUTS`. */
export const GALLERY_PHONE_LAYOUTS = PHONE_GRID_LAYOUTS;
export type GalleryPhoneLayout = (typeof GALLERY_PHONE_LAYOUTS)[number];

const galleryElement = z.strictObject({
  ...elementBase,
  kind: z.literal("gallery"),
  mediaIds: z
    .array(mediaId)
    .max(SITE_LIMITS.galleryImages, `В галерее не больше ${SITE_LIMITS.galleryImages} фото`),
  perRow,
  /**
   * На телефоне (v4): stack — стопкой в одну колонку, grid2 — по две, scroll —
   * лентой вбок. Прежнее «stack» v3 рисовалось по две — подъём переводит его в grid2.
   */
  phoneLayout: z.enum(GALLERY_PHONE_LAYOUTS),
});

/**
 * Видео — только файл из медиатеки магазина (SB97; OQ-127 закрыт решением
 * пользователя 2026-10-09: видеоплощадки по ссылке не поддерживаем).
 */
const videoElement = z.strictObject({
  ...elementBase,
  kind: z.literal("video"),
  mediaId,
  posterMediaId: mediaId.nullable(),
});

const audioElement = z.strictObject({
  ...elementBase,
  kind: z.literal("audio"),
  mediaId,
  title: optionalText(T.audioTitle).optional(),
});

/**
 * Рамка кнопки (v4): толщина 0–4 px, цвет — нет ключа — как текст
 * (`currentColor`). Нет `border` — как у вида: у контурной 1 px, у
 * остальных без рамки (это и есть «как было», перевода нет).
 */
export const sectionButtonBorderSchema = z.strictObject({
  width: z.number().int().min(LIM.buttonBorder.min).max(LIM.buttonBorder.max),
  color: siteColorRefSchema.optional(),
});
export type SectionButtonBorder = z.infer<typeof sectionButtonBorderSchema>;

const buttonElement = z.strictObject({
  ...elementBase,
  kind: z.literal("button"),
  label: requiredText(T.label, "Впишите текст кнопки"),
  target: sectionTargetSchema,
  variant: z.enum(["primary", "outline", "link"]),
  /** Нет ключа — без значка. */
  icon: z.enum(SITE_ICON_KEYS).optional(),
  iconPosition: z.enum(["left", "right"]),
  /** На телефоне — во всю ширину колонки. */
  phoneFull: z.boolean(),
  newTab: z.boolean(),
  /**
   * «В строку»: соседние кнопки с `inline: true` витрина ставит в одну строку
   * (на телефоне — с переносом). Только это; контейнеров нет. Нет ключа — false
   * (решение оркестратора 2026-10-09: две кнопки первого экрана «Как было»).
   */
  inline: z.boolean().optional(),
  /**
   * Заливка (v4) — действует только у `primary`; нет ключа — `--site-button` и
   * наведение темы. У других видов хранится и не рисуется (данные при смене
   * вида не теряются).
   */
  fill: siteColorRefSchema.optional(),
  border: sectionButtonBorderSchema.optional(),
  /** Высота, px (`min-height`, текст растит кнопку); нет ключа — 48. */
  height: ranged(LIM.buttonHeight).optional(),
  /** Цвет текста — `typo.main.color`; нет — «Авто: по заливке» (у контурной и ссылки — цвет ссылки). */
  typo: typoFor("button"),
});

export const sectionTileSchema = z.strictObject({
  /** null — место под фото из заготовки. */
  mediaId: mediaId.nullable(),
  title: optionalText(T.tileTitle),
  text: optionalText(T.tileText).optional(),
  /** Текст кнопки на плитке; нет ключа — плитка целиком ссылка. */
  buttonLabel: optionalText(T.label).optional(),
  /** null — плитка без ссылки (заготовка, ещё не настроена). */
  target: sectionTargetSchema.nullable(),
});

/**
 * Плитки на телефоне: stack — стопкой, scroll — лентой вбок, grid2 — по две в
 * ряд (так шли плитки каталога на проде на 360, сверка 2026-10-10).
 * Алиас `PHONE_GRID_LAYOUTS` (element-grid.ts) — один источник набора (SB10).
 */
export const SECTION_TILE_PHONE_LAYOUTS = PHONE_GRID_LAYOUTS;

/**
 * Текст поверх фото плитки (SB24, дополнение 2026-10-11): light — светлый
 * текст с затемнением фото (как было), dark — тёмный текст без затемнения.
 */
export const TILE_TONES = ["light", "dark"] as const;
export type TileTone = (typeof TILE_TONES)[number];
export const TILE_TONE_LABEL = "Текст поверх фото";
export const TILE_TONE_OPTIONS: Readonly<Record<TileTone, string>> = {
  light: "Светлый текст с затемнением",
  dark: "Тёмный текст без затемнения",
};

/**
 * Высота плитки у плиток и категорий (SB24; v4 — SB120 этап 3а) — пропорцией
 * ширина / высота 0,5–2, не пикселями: плитка тянется по ширине колонки, а
 * пропорция держит одинаковый вид на 360 и на 1280. Ступени v3 (низкая /
 * обычная / высокая) — подсказки 1,5 / 1 / 0,75 (`TILE_ASPECT`, legacy-steps.ts).
 */
const tileAspect = ranged(LIM.tileAspect);

const tilesElement = z.strictObject({
  ...elementBase,
  kind: z.literal("tiles"),
  cols: perRowWide,
  /** overlay — текст поверх фото, below — фото сверху, текст под ним. */
  look: z.enum(["overlay", "below"]),
  /** Нет ключа в старых данных — «light» (как было); read.ts дополняет из умолчания. */
  tone: z.enum(TILE_TONES),
  /** Пропорция плитки ширина / высота 0,5–2 (v4; v3 «высота» low/normal/high → 1,5 / 1 / 0,75). */
  aspect: tileAspect,
  phoneLayout: z.enum(SECTION_TILE_PHONE_LAYOUTS),
  /**
   * Затемнение фото при «Текст поверх фото», 0–80 % (этап 3б); нет ключа —
   * по набору: светлый 45, тёмный 0 (`tileShadeDefault`); градиент 45/10/35
   * сохраняет форму (`tileShadeGradient`).
   */
  shade: ranged(LIM.tileShade).optional(),
  items: z.array(sectionTileSchema).max(SITE_LIMITS.tiles, `Плиток не больше ${SITE_LIMITS.tiles}`),
  /** Части: название (заголовков) и подпись с кнопкой (основного текста). */
  typo: typoFor("tiles"),
});

const productsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("products"),
  /** new — новинки по дате (сами), badge — с ручным бейджем, manual — выбранные. */
  source: z.enum(["new", "badge", "manual"]),
  badge: z.enum(SECTION_PRODUCT_BADGES).nullable(),
  /** Порядок показа при `manual`; при других источниках не используется. */
  ids: z
    .array(entityId)
    .max(SITE_LIMITS.manualProducts, `Товаров вручную — не больше ${SITE_LIMITS.manualProducts}`),
  /** Сколько показать при `new` / `badge`. */
  limit: z.number().int().min(1).max(SITE_LIMITS.manualProducts),
  perRow,
  /** На телефоне: лентой, по две или (этап 3б) стопкой в одну колонку. */
  phoneLayout: z.enum(PHONE_GRID_LAYOUTS),
});

const categoriesElement = z.strictObject({
  ...elementBase,
  kind: z.literal("categories"),
  /** auto — категории верхнего уровня по порядку каталога; manual — выбранные. */
  source: z.enum(["auto", "manual"]),
  ids: z
    .array(entityId)
    .max(SITE_LIMITS.catalogCategoriesAutoLimit, `Категорий — не больше ${SITE_LIMITS.catalogCategoriesAutoLimit}`),
  limit: z.number().int().min(1).max(SITE_LIMITS.catalogCategoriesAutoLimit),
  cols: perRowWide,
  look: z.enum(["overlay", "below"]),
  /** Пропорция плитки ширина / высота 0,5–2 (v4; как у плиток). */
  aspect: tileAspect,
  /** На телефоне — как у плиток: стопкой, лентой или по две в ряд. */
  phoneLayout: z.enum(SECTION_TILE_PHONE_LAYOUTS),
  /** Подпись категории (этап 3б); нет ключа — основным текстом. */
  typo: typoFor("categories"),
});

const articlesElement = z.strictObject({
  ...elementBase,
  kind: z.literal("articles"),
  /** latest — свежие статьи блога, manual — выбранные. */
  source: z.enum(["latest", "manual"]),
  ids: z.array(entityId).max(SITE_LIMITS.articles, `Статей — не больше ${SITE_LIMITS.articles}`),
  limit: z.number().int().min(1).max(SITE_LIMITS.articles),
  /** Что показывать в карточке статьи. */
  show: z.strictObject({ category: z.boolean(), author: z.boolean(), date: z.boolean() }),
  /** В ряд на ПК 1–6 (этап 3б); нет ключа — как было: 3 на ПК, 2 на планшете. */
  perRow: perRow.optional(),
  /** На телефоне (этап 3б); нет ключа — как было: стопкой. */
  phoneLayout: z.enum(PHONE_GRID_LAYOUTS).optional(),
  /** Части: рубрика (надписи), название (заголовков), анонс с датой и автором (основного текста). */
  typo: typoFor("articles"),
});

const reviewsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("reviews"),
  /** Отзывы о товарах или о магазине; вписать текстом нельзя (SB25). */
  source: z.enum(["product", "site"]),
  pick: z.enum(["best", "recent", "manual"]),
  ids: z.array(entityId).max(SITE_LIMITS.manualProducts, `Отзывов — не больше ${SITE_LIMITS.manualProducts}`),
  limit: z.number().int().min(1).max(12),
  /** Лентой (carousel) или сеткой (grid). */
  layout: z.enum(["carousel", "grid"]),
  /**
   * В ряд 1–4 (этап 3б); нет ключа — 3: лентой по 32 % (`reviewStripWidth`,
   * 96 / N), сеткой — 3 на ПК. На телефоне лента — 85 %.
   */
  perRow: reviewsPerRow.optional(),
  /** Части: заголовок отзыва (заголовков), текст и имя (основного текста). */
  typo: typoFor("reviews"),
});

export const sectionFaqItemSchema = z.strictObject({
  q: requiredText(T.faqQuestion, "Впишите вопрос"),
  a: requiredText(T.faqAnswer, "Впишите ответ"),
});

const faqElement = z.strictObject({
  ...elementBase,
  kind: z.literal("faq"),
  typo: typoFor("faq"),
  items: z
    .array(sectionFaqItemSchema)
    .max(SITE_LIMITS.faqItems, `Вопросов не больше ${SITE_LIMITS.faqItems}`),
  firstOpen: z.boolean(),
  /** Разделитель между вопросами (этап 3б); нет ключа — 1 px цветом линии. */
  divider: sectionFaqDividerSchema.optional(),
  /** Значок (этап 3б); нет ключа — «+» с поворотом (как было). */
  icon: z.enum(FAQ_ICONS).optional(),
});

const contactsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("contacts"),
  typo: typoFor("contacts"),
  /**
   * Какие контакты из «Настроек сайта» показать; вписать вручную нельзя (SB25).
   * Кроме видов списка контактов — производные («Часы работы» из
   * `workingHours`, phase19.28 этап А; contacts-derived.ts).
   */
  kinds: z
    .array(z.enum(SECTION_CONTACT_KIND_KEYS))
    .min(1, "Отметьте хотя бы один контакт")
    .max(SECTION_CONTACT_KIND_KEYS.length),
});

/** SB26: добавляется, только когда у магазина работает рассылка (проверяет сервер). */
const newsletterElement = z.strictObject({
  ...elementBase,
  kind: z.literal("newsletter"),
  /** Текст формы (этап 3б); нет ключа — основным текстом. */
  typo: typoFor("newsletter"),
});

/**
 * Прежние ступени смены объявлений, секунд. С этапа 3б смена — целые 2–30
 * (`SECTION_LOOK_LIMITS.intervalSec`); ступени остаются подсказками.
 */
export const SECTION_ANNOUNCEMENT_INTERVALS = [4, 6, 8, 10] as const;

export const sectionAnnouncementSchema = z.strictObject({
  /** Уникален в пределах элемента (порядок и перетаскивание в кабинете). */
  id: z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/, "id объявления — латиница, цифры, «-» и «_»")
    .min(1)
    .max(T.elementId),
  text: requiredText(T.announcement, "Впишите текст объявления"),
  /** Куда ведёт объявление; нет ключа или null — просто текст. */
  target: sectionTargetSchema.nullable().optional(),
});
export type SectionAnnouncement = z.infer<typeof sectionAnnouncementSchema>;

/**
 * «Строка объявлений» с ротацией (решения пользователя 2026-10-09 и
 * 2026-10-10): объявления сменяют друг друга через `intervalSec`.
 *   source = "promotions" — действующие акции из раздела «Акции» (SB25): до
 *     `limit` штук, текст строит API по `promoTextMode` (auto — «Скидка 10 %
 *     на «…» до 20 октября», nameOnly — только название); `items` пусты.
 *     Нет акций — секция не показывается (SB21).
 *   source = "manual" — вписанные пункты `items` (1–5).
 * `limit` и `promoTextMode` хранятся всегда (умолчания 5 и auto), при manual
 * не используются.
 * Вид (решение пользователя 2026-10-10): strip — полоса с ротацией
 * (`intervalSec`), cards — карточки (`perRow`, `phoneLayout`), list — списком.
 * Поля чужого вида хранятся всегда и витриной не используются. Прежний блок PROMO_STRIP сюда не поднимается — его
 * переносит пакет данных магазина.
 */
const announcementsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("announcements"),
  source: z.enum(["manual", "promotions"]),
  /** Вписанные пункты; при `manual` — 1–5 (проверяет superRefine), при `promotions` — пусто. */
  items: z
    .array(sectionAnnouncementSchema)
    .max(SITE_LIMITS.announcements, `Не больше ${SITE_LIMITS.announcements} объявлений`),
  /** Сколько акций показать (`promotions`). */
  limit: z.number().int().min(1).max(SITE_LIMITS.announcements),
  /** Текст акции: auto — условия и срок, nameOnly — только название (`promotions`). */
  promoTextMode: z.enum(["auto", "nameOnly"]),
  layout: z.enum(["strip", "cards", "list"]),
  /** Смена объявлений, целые секунд 2–30 (этап 3б; было 4/6/8/10) — только для `strip`. */
  intervalSec: z.number().int().min(LIM.intervalSec.min).max(LIM.intervalSec.max),
  /** Карточек в ряд на ПК — только для `cards`. */
  perRow,
  /** Карточки на телефоне: стопкой, лентой или (этап 3б) по две — только для `cards`. */
  phoneLayout: z.enum(PHONE_GRID_LAYOUTS),
  /** Значок перед текстом; нет ключа или null — без значка. */
  icon: z.enum(SITE_ICON_KEYS).nullable().optional(),
  /** Части: заголовок карточки (заголовков) и текст (основного); у полосы и списка — только текст. */
  typo: typoFor("announcements"),
});

/** id пункта внутри элемента (показатель, столбец, строка таблицы). */
const itemId = z
  .string()
  .regex(/^[A-Za-z0-9_-]+$/, "id — латиница, цифры, «-» и «_»")
  .min(1)
  .max(T.elementId);

export const sectionStatSchema = z.strictObject({
  id: itemId,
  /** Крупное значение: «10 лет», «500+». */
  value: requiredText(T.statValue, "Впишите значение"),
  label: requiredText(T.statLabel, "Впишите подпись"),
});
export type SectionStat = z.infer<typeof sectionStatSchema>;

/** «Цифры» (решение пользователя 2026-10-10): 1–4 показателя крупно с подписью. */
const statsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("stats"),
  items: z
    .array(sectionStatSchema)
    .min(1, "Нужен хотя бы один показатель")
    .max(SITE_LIMITS.statsItems, `Не больше ${SITE_LIMITS.statsItems} показателей`),
  /** В ряд на ПК 1–6 (этап 3б; было 2–4); хранится явно (умолчание — число показателей). */
  perRow,
  /**
   * Основа размера числа: m — 36/30 px, l — 60/36 px (классы Tailwind задают и
   * межстрочный интервал, поэтому основа остаётся; db-design-sb120-stage3 §1).
   * `typo.number.size` — необязательная замена поверх основы.
   */
  size: z.enum(["m", "l"]),
  /** Части: число (заголовков) и подпись (основного текста). */
  typo: typoFor("stats"),
});

export const sectionTableColumnSchema = z.strictObject({
  id: itemId,
  title: optionalText(T.tableTitle),
});

export const sectionTableRowSchema = z.strictObject({
  id: itemId,
  /** Ячейки по порядку столбцов; длина = число столбцов (проверяет superRefine). */
  cells: z.array(optionalText(T.tableCell)).max(SITE_LIMITS.tableColumns),
});

/**
 * «Таблица» (решение пользователя 2026-10-10): размеры, тарифы, сравнение.
 * На телефоне: scroll — прокрутка вбок, stack — каждая строка карточкой
 * «заголовок столбца: значение».
 */
const tableElement = z.strictObject({
  ...elementBase,
  kind: z.literal("table"),
  /** Цвет части не действует в карточках таблицы на телефоне (там своя подложка). */
  typo: typoFor("table"),
  columns: z
    .array(sectionTableColumnSchema)
    .min(1, "Нужен хотя бы один столбец")
    .max(SITE_LIMITS.tableColumns, `Столбцов не больше ${SITE_LIMITS.tableColumns}`),
  rows: z.array(sectionTableRowSchema).max(SITE_LIMITS.tableRows, `Строк не больше ${SITE_LIMITS.tableRows}`),
  firstColumnBold: z.boolean(),
  phoneLayout: z.enum(["scroll", "stack"]),
  /** Фон шапки (этап 3б); нет ключа — без фона (как было). Контраст текста к нему — предупреждение. */
  head: siteColorRefSchema.optional(),
  /** Полосы через строку (этап 3б); нет ключа — без полос. */
  stripe: siteColorRefSchema.optional(),
  /** Линии (этап 3б); нет ключа — 1 px между строками и 2 px под шапкой цветом линии. */
  lines: sectionTableLinesSchema.optional(),
});

// ── Элементы данных товара (Р2а; SB34–SB37, uiux-design-r2a §3) ──

/** Хлебные крошки: Главная › Каталог › категории › товар. Настроек нет. */
const breadcrumbsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("breadcrumbs"),
  typo: typoFor("breadcrumbs"),
});

const productGalleryElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-gallery"),
  /** Миниатюры под фото, слева (`thumbs-side`), справа (этап 3б) или все фото сеткой. */
  layout: z.enum(PRODUCT_GALLERY_LAYOUTS),
  /** Открывать фото крупно по нажатию. */
  zoom: z.boolean(),
});

/** Название товара — всегда H1 страницы (обязательный элемент). */
const productTitleElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-title"),
  /** Звёзды и число отзывов под названием (показываются с 3 отзывов — правило витрины). */
  showRating: z.boolean(),
  /** Части: название (H1, заголовков), категория (акцента), рейтинг (основного текста). */
  typo: typoFor("product-title"),
});

/** Цена и покупка (SB37, обязательный): фасовки, цена, старая цена по P3, количество, кнопка. */
const productBuyElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-buy"),
  /** Фасовки: кнопками или списком. */
  skuPicker: z.enum(["chips", "select"]),
  showQuantity: z.boolean(),
  buttonWidth: z.enum(["full", "auto"]),
});

const productBadgesElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-badges"),
});

/** Короткое описание (подзаголовок товара). */
const productShortElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-short"),
  typo: typoFor("product-short"),
});

const productDescriptionElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-description"),
  /** Длинное описание свёрнуто с «Читать дальше». */
  collapsible: z.boolean(),
  /** Части: подпись «Описание» (заголовков) и текст (основного текста). */
  typo: typoFor("product-description"),
});

/**
 * Строки «Сведений о товаре», которые владелец включает и выключает по одной
 * (SB35, приёмка Р2а, решение пользователя 2026-10-11). Ключи — как у строк
 * витрины (apps/web/src/lib/catalog/product-facts.ts; документы производителя
 * там идут строками `cert-N` — их все включает `certificates`). Порядок —
 * порядок переключателей в кабинете. Замков нет: `legal` — только пометка
 * `PRODUCT_FACT_LEGAL_NOTE` у строк ЗоЗПП ст. 26.1 п. 2 (срок годности,
 * производитель; для пищевых и косметики — состав и аллергены). Плашек «БАД»
 * и «Не для употребления в пищу» здесь нет: они выводятся всегда при признаке
 * у товара.
 */
export const PRODUCT_FACT_ROWS = [
  { key: "composition", label: "Состав", legal: true },
  { key: "shelfLife", label: "Срок годности", legal: true },
  { key: "storage", label: "Условия хранения", legal: false },
  { key: "allergens", label: "Аллергены", legal: true },
  { key: "nutrition", label: "Пищевая ценность", legal: false },
  { key: "netWeight", label: "Масса нетто", legal: false },
  { key: "age", label: "Возрастное ограничение", legal: false },
  { key: "producer", label: "Производитель", legal: true },
  { key: "declaration", label: "Декларация", legal: false },
  { key: "certificates", label: "Сертификаты и СГР", legal: false },
] as const satisfies ReadonlyArray<{ key: string; label: string; legal: boolean }>;
export type ProductFactRowKey = (typeof PRODUCT_FACT_ROWS)[number]["key"];

/** Подпись пометки у строк с `legal: true` — одна на кабинет и подсказки. */
export const PRODUCT_FACT_LEGAL_NOTE = "по закону показывается до покупки";

/**
 * Какие строки выводить: ключ на каждую строку `PRODUCT_FACT_ROWS`, лишний
 * ключ — ошибка записи. Старые элементы без `rows` (черновики и ревизии до
 * 2026-10-11) читаются через `readSectionPayload`: недостающие ключи берутся
 * из умолчания элемента (defaults.ts — всё включено), поэтому показывают всё,
 * как раньше.
 */
const productFactRowsSchema = z.strictObject(
  Object.fromEntries(PRODUCT_FACT_ROWS.map((row) => [row.key, z.boolean()])) as Record<ProductFactRowKey, z.ZodBoolean>,
);
export type ProductFactRows = z.infer<typeof productFactRowsSchema>;

/** Все строки включены — умолчание `rows` (каждый вызов — новый объект). */
export function defaultProductFactRows(): ProductFactRows {
  return Object.fromEntries(PRODUCT_FACT_ROWS.map((row) => [row.key, true])) as ProductFactRows;
}

/**
 * Сведения о товаре (SB35, обязательный): состав, сроки, хранение,
 * производитель, декларация, плашки. Состав — всегда обычная строка списка:
 * настройка «Состав — отдельной плашкой» (`highlightComposition`) снята
 * решением пользователя 2026-10-11; в старых данных ключ лишний — читатель
 * снимает его (read.ts, запись в `replaced`), запись с ним отклоняется.
 */
const productFactsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-facts"),
  look: z.enum(["rows", "accordion"]),
  /** Какие строки выводить (SB35, решение 2026-10-11); по умолчанию все. */
  rows: productFactRowsSchema,
});

/** Характеристики из `Product.attributes` (решение пользователя 2026-10-12). */
const productSpecsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-specs"),
  look: z.enum(["table", "rows"]),
});

/** Метки товара (решение пользователя 2026-10-12): облаком или строкой. */
const productTagsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-tags"),
  look: z.enum(["cloud", "row"]),
});

const productReviewsElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-reviews"),
  /** Сколько отзывов показать; дальше — ссылка «Все отзывы». */
  limit: z.number().int().min(1).max(SITE_LIMITS.productReviews),
});

/** «На телефоне»: стопкой по одному, лентой вбок или по два в ряд — алиас `PHONE_GRID_LAYOUTS`. */
export const PRODUCT_RELATED_PHONE_LAYOUTS = PHONE_GRID_LAYOUTS;

const productRelatedElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-related"),
  limit: z.number().int().min(2).max(SITE_LIMITS.productRelated),
  perRow,
  phoneLayout: z.enum(PRODUCT_RELATED_PHONE_LAYOUTS),
});

/**
 * Продавец (SB36): реквизиты из профиля юрлица; свёрнутой строкой или
 * полностью. На этапе 1 необязательный (решение 2026-10-11): можно скрыть и
 * удалить — реквизиты магазина есть в подвале (SB19).
 */
const productSellerElement = z.strictObject({
  ...elementBase,
  kind: z.literal("product-seller"),
  look: z.enum(["compact", "full"]),
});

export const sectionElementSchema = z.discriminatedUnion("kind", [
  headingElement,
  eyebrowElement,
  textElement,
  imageElement,
  galleryElement,
  videoElement,
  audioElement,
  buttonElement,
  tilesElement,
  productsElement,
  categoriesElement,
  articlesElement,
  reviewsElement,
  faqElement,
  contactsElement,
  newsletterElement,
  announcementsElement,
  statsElement,
  tableElement,
  breadcrumbsElement,
  productGalleryElement,
  productTitleElement,
  productBuyElement,
  productBadgesElement,
  productShortElement,
  productDescriptionElement,
  productFactsElement,
  productSpecsElement,
  productTagsElement,
  productReviewsElement,
  productRelatedElement,
  productSellerElement,
]);

/** Схема элемента по виду — для починки по полям (read.ts) и формы кабинета. */
export const SECTION_ELEMENT_SCHEMAS = {
  heading: headingElement,
  eyebrow: eyebrowElement,
  text: textElement,
  image: imageElement,
  gallery: galleryElement,
  video: videoElement,
  audio: audioElement,
  button: buttonElement,
  tiles: tilesElement,
  products: productsElement,
  categories: categoriesElement,
  articles: articlesElement,
  reviews: reviewsElement,
  faq: faqElement,
  contacts: contactsElement,
  newsletter: newsletterElement,
  announcements: announcementsElement,
  stats: statsElement,
  table: tableElement,
  breadcrumbs: breadcrumbsElement,
  "product-gallery": productGalleryElement,
  "product-title": productTitleElement,
  "product-buy": productBuyElement,
  "product-badges": productBadgesElement,
  "product-short": productShortElement,
  "product-description": productDescriptionElement,
  "product-facts": productFactsElement,
  "product-specs": productSpecsElement,
  "product-tags": productTagsElement,
  "product-reviews": productReviewsElement,
  "product-related": productRelatedElement,
  "product-seller": productSellerElement,
} as const satisfies Record<SectionElementKind, z.ZodObject>;

export type SectionElement = z.infer<typeof sectionElementSchema>;
/** Элемент данного вида: `SectionElementOf<"button">`. */
export type SectionElementOf<K extends SectionElementKind> = Extract<SectionElement, { kind: K }>;

/** Сужение строки до вида элемента v2 (общего или данных товара; контекст — contexts.ts). */
export function isSectionElementKind(kind: unknown): kind is SectionElementKind {
  return typeof kind === "string" && (SECTION_ELEMENT_KINDS_ALL as readonly string[]).includes(kind);
}

/** Вид — элемент данных товара (Р2а). */
export function isProductElementKind(kind: unknown): kind is ProductElementKind {
  return typeof kind === "string" && (PRODUCT_ELEMENT_KINDS as readonly string[]).includes(kind);
}

/** Вид — обязательный элемент шаблона страницы товара (SB34). */
export function isProductRequiredKind(kind: unknown): kind is ProductRequiredKind {
  return typeof kind === "string" && (PRODUCT_TEMPLATE_REQUIRED as readonly string[]).includes(kind);
}

// ── Шапка раздела ──

export const sectionHeaderSchema = z.strictObject({
  eyebrow: optionalText(T.eyebrow).optional(),
  title: requiredText(T.heading, "Впишите заголовок раздела"),
  level: headingLevel,
  icon: z.enum(SITE_ICON_KEYS).optional(),
  /** Цвет шапки (надпись, заголовок, значок); нет ключа — «Авто» по фону. */
  color: siteTextColorSchema.optional(),
  /**
   * Части шапки (v4): надпись, заголовок, ссылка справа. Цвет части —
   * частная замена `color`: часть → шапка → «Авто».
   */
  typo: typoSchemaOf(HEADER_TYPO_PART_KEYS),
  /**
   * Линия от заголовка до ссылки справа: толщина 0–4 px (0 — нет линии), цвет —
   * нет ключа — `--site-line`. На телефоне и при выравнивании по центру не
   * рисуется (SectionHeader.tsx).
   */
  line: z.strictObject({
    width: z.number().int().min(LIM.lineWidth.min).max(LIM.lineWidth.max),
    color: siteTextColorSchema.optional(),
  }),
  /** Ссылка справа («Все новинки →»). */
  link: z.strictObject({ label: requiredText(T.label, "Впишите текст ссылки"), target: sectionTargetSchema }).optional(),
  /**
   * Выравнивание заголовка; нет ключа — слева. Дополнение к db-design-r2:
   * у шапок v1 выравнивание было (`headingAlign`), подъём его сохраняет.
   */
  align: z.enum(["left", "center", "right"]).optional(),
});
export type SectionHeader = z.infer<typeof sectionHeaderSchema>;

// ── Положение содержимого (SB120 этап 1, uiux-sb120-stage1 §2.8) ──

/** По вертикали: сверху / по центру / снизу / растянуть на всю высоту. */
export const SECTION_PLACE_Y = ["top", "center", "bottom", "stretch"] as const;
export type SectionPlaceY = (typeof SECTION_PLACE_Y)[number];
/** По горизонтали: слева / по центру / справа. */
export const SECTION_PLACE_X = ["left", "center", "right"] as const;
export type SectionPlaceX = (typeof SECTION_PLACE_X)[number];

/**
 * Положение содержимого в родителе. Нет ключа: у секции — по центру и по
 * центру (section.css, `mx-auto`); у колонки — y как `columns.valign`,
 * x — слева; на телефоне — как на ПК.
 */
export const sectionPlaceSchema = z.strictObject({
  y: z.enum(SECTION_PLACE_Y).optional(),
  x: z.enum(SECTION_PLACE_X).optional(),
});
export type SectionPlace = z.infer<typeof sectionPlaceSchema>;

// ── Колонки ──

/** Внутренние отступы колонки, px; нет ключа — M сайта при своём фоне (× доля темы на телефоне). */
export const sectionColumnPadSchema = z.strictObject({
  top: px(LIM.side.max).optional(),
  bottom: px(LIM.side.max).optional(),
  left: px(LIM.side.max).optional(),
  right: px(LIM.side.max).optional(),
});
export type SectionColumnPad = z.infer<typeof sectionColumnPadSchema>;

export const sectionColumnSchema = z.strictObject({
  /** Свой фон колонки — роль или свой цвет (ColorField fill); нет ключа — прозрачная. */
  bg: siteSurfaceFillSchema.optional(),
  /** Своё положение в ряду и выравнивание элементов; нет ключа — как у всех колонок. */
  place: sectionPlaceSchema.optional(),
  pad: sectionColumnPadSchema.optional(),
  elements: z
    .array(sectionElementSchema)
    .max(SITE_LIMITS.elementsPerColumn, `В колонке не больше ${SITE_LIMITS.elementsPerColumn} элементов`),
});
export type SectionColumn = z.infer<typeof sectionColumnSchema>;

const shareSchema = z.number().int().min(LIM.shares.min).max(LIM.shares.max);

export const sectionColumnsSchema = z.strictObject({
  /** Доли колонок из 12: по одной на колонку, сумма 12 (проверяет superRefine). */
  shares: z.array(shareSchema).min(1).max(4),
  /** Между колонками, px; нет ключа — M сайта (32 × плотность). */
  gap: px(LIM.gap.max).optional(),
  /** Между элементами в колонке, px; нет ключа — M сайта. */
  elementGap: px(LIM.gap.max).optional(),
  /** Колонки в ряду по вертикали — общее умолчание «Все колонки»; stretch — на всю высоту ряда. */
  valign: z.enum(SECTION_PLACE_Y),
  /** Порядок колонок на телефоне — перестановка 1..N (superRefine); нет ключа — по порядку. */
  phoneOrder: z.array(z.number().int().min(1).max(4)).min(1).max(4).optional(),
  items: z
    .array(sectionColumnSchema)
    .min(1, "В секции должна быть хотя бы одна колонка")
    .max(4, "Колонок не больше 4"),
});
export type SectionColumns = z.infer<typeof sectionColumnsSchema>;

// ── Оформление ──

/** Растворение края фото фона. */
export const SECTION_FADE_SIDES = ["none", "left", "right", "top", "bottom"] as const;

export const sectionBgSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("role"), role: z.enum(SECTION_BG_ROLES) }),
  z.strictObject({
    kind: z.literal("media"),
    mediaId,
    /** Другое фото для телефона (вопрос 8, 2026-10-18); нет ключа — то же фото. */
    mediaIdPhone: mediaId.optional(),
    focus: sectionFocusSchema,
    /** Фокус на телефоне; нет ключа — как на ПК. */
    focusPhone: sectionFocusSchema.optional(),
    /** Масштаб кадра 1–4 (кадр не режет файл, uiux §2.4); нет ключа — 1. */
    zoom: ranged(LIM.zoom).optional(),
    /** Масштаб на телефоне; нет ключа — как на ПК. */
    zoomPhone: ranged(LIM.zoom).optional(),
    /** Растворить край фото в фон страницы. */
    fade: z.enum(SECTION_FADE_SIDES),
    /** Ширина растворения, %; нет ключа — 45 %. */
    fadeWidth: ranged(LIM.fadeWidth).optional(),
    /** Затемнение 0–0,6, как у фона страницы в теме. */
    dim: z.number().min(0).max(0.6),
    /**
     * Набор цветов текста на фото: «Тёмный» / «Светлый» (заголовок, текст,
     * ссылка разом). Дополнение к db-design-r2: без него витрина не знает,
     * чем писать по фото.
     */
    textTone: z.enum(["dark", "light"]),
    /**
     * «Свой» цвет всего текста секции поверх набора (решение 2026-10-18 (в));
     * элементы со своим цветом его не наследуют. Нет ключа — по набору.
     */
    textColor: siteTextColorSchema.optional(),
    /** «Выход за край»: фото заходит на соседние блоки — вверх/вниз px, вбок до края окна. */
    bleed: z.strictObject({
      top: px(LIM.space.max),
      bottom: px(LIM.space.max),
      side: z.enum(["none", "left", "right"]),
    }),
  }),
  z.strictObject({ kind: z.literal("transparent") }),
  /**
   * «Свой цвет» (SB8, дополнение 2026-10-11): hex как у ролей темы. Тон текста —
   * по светлоте (surfaceToneOf из site-theme/surface-fill.ts): на тёмном — светлый.
   */
  z.strictObject({ kind: z.literal("custom"), color: siteFillColorSchema }),
]);
export type SectionBg = z.infer<typeof sectionBgSchema>;
export type SectionMediaBg = Extract<SectionBg, { kind: "media" }>;

export const sectionDecorSchema = z.strictObject({
  /** Рисунок PNG/SVG из медиатеки. */
  mediaId,
  /** Место рисунка, % от левого верхнего угла рамки (секции или колонки). */
  x: ranged(LIM.decorPosition),
  y: ranged(LIM.decorPosition),
  /** Ширина на ПК, px. */
  size: ranged(LIM.decorSize),
  /** Ширина на телефоне, px; нет ключа — ПК × доля темы. */
  sizePhone: ranged(LIM.decorSize).optional(),
  hideOnPhone: z.boolean(),
  /** «Выход за край» секции (uiux-design-r2 §2, группа «Рисунки»). */
  bleed: z.boolean(),
  /** Привязка: null — к секции, N — к колонке N (1–4). */
  column: z.number().int().min(1).max(4).nullable(),
});
export type SectionDecor = z.infer<typeof sectionDecorSchema>;

/**
 * Отступы одной стороны набора, px. Нет ключа — «как у сайта»: снаружи L;
 * внутри сверху/снизу — M у подложки, L на своём фоне, 0 у прозрачной;
 * внутри слева/справа — 0 при «Во всю», иначе поля сетки (22 · 40 · 70 по
 * ширине экрана). «⛓ одинаково» не хранится: связь = innerLeft === innerRight.
 */
const spaceSidesShape = {
  outerTop: px(LIM.space.max).optional(),
  outerBottom: px(LIM.space.max).optional(),
  innerTop: px(LIM.space.max).optional(),
  innerBottom: px(LIM.space.max).optional(),
  innerLeft: px(LIM.side.max).optional(),
  innerRight: px(LIM.side.max).optional(),
};

/** Поля отступов секции, у которых есть «своё на телефоне». */
export const SECTION_SPACE_SIDES = ["outerTop", "outerBottom", "innerTop", "innerBottom", "innerLeft", "innerRight"] as const;
export type SectionSpaceSide = (typeof SECTION_SPACE_SIDES)[number];

/** «Своё» на телефоне для отдельных полей; нет ключа — ПК × доля. */
export const sectionSpaceSidesSchema = z.strictObject(spaceSidesShape);
export type SectionSpaceSides = z.infer<typeof sectionSpaceSidesSchema>;

export const sectionSpaceSchema = z.strictObject({
  ...spaceSidesShape,
  phone: sectionSpaceSidesSchema.optional(),
  /** Доля телефона от ПК для шести полей выше; нет ключа — доля темы (`spacing.phoneFactor`). */
  phoneFactor: ranged(LIM.phoneFactor).optional(),
  /** «Наезд на блок выше», px (0 — нет). */
  overlap: px(LIM.space.max),
  /** «Слить с блоком выше» — общий фон без шва. */
  mergeWithPrev: z.boolean(),
});
export type SectionSpace = z.infer<typeof sectionSpaceSchema>;

/**
 * Высота секции (uiux-sb120-stage1 «Дополнение» §2.1): content — по
 * содержимому; photo — по пропорциям фото фона (нет `aspect` — пропорция
 * файла; на телефоне `aspect` — «По кадру телефона»); number — px;
 * screen — % окна без шапки. В любом режиме содержимое главнее: высота —
 * нижняя граница, текст растит блок.
 */
export const sectionHeightSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("content") }),
  z.strictObject({ kind: z.literal("photo"), aspect: aspectSchema.optional() }),
  z.strictObject({ kind: z.literal("number"), value: px(LIM.heightPx.max) }),
  z.strictObject({ kind: z.literal("screen"), value: ranged(LIM.heightScreen) }),
]);
export type SectionHeight = z.infer<typeof sectionHeightSchema>;

/** Подложка: «На подложке карточки сайта»; скругление px и тень % — нет ключа — как у сайта. */
export const sectionCardSchema = z.strictObject({
  on: z.boolean(),
  radius: ranged(LIM.cardRadius).optional(),
  shadow: ranged(LIM.cardShadow).optional(),
});
export type SectionCard = z.infer<typeof sectionCardSchema>;

/** Ширина блока: px или «Во всю» (отдельный режим, без предела и боков). */
export const sectionContainerSchema = z.union([ranged(LIM.container), z.literal("full")]);
export type SectionContainer = z.infer<typeof sectionContainerSchema>;

export const sectionLookSchema = z.strictObject({
  bg: sectionBgSchema,
  /** Нет ключа — по сетке (1280). */
  container: sectionContainerSchema.optional(),
  /**
   * «Фон за рамку сайта» (SB120 этап 2, uiux-sb120-stage2 §3): при рамке «По
   * центру» фон блока идёт до краёв окна, ширина считается от окна, а не от
   * рамки. Нет ключа — в рамке; `false` не хранится. Версия секции остаётся 3.
   */
  outsideFrame: z.literal(true).optional(),
  card: sectionCardSchema,
  height: sectionHeightSchema,
  /** Высота на телефоне; нет ключа — как на ПК. */
  heightPhone: sectionHeightSchema.optional(),
  /** Положение содержимого в блоке; нет ключа — по центру и по центру. */
  place: sectionPlaceSchema.optional(),
  /** На телефоне; нет ключа — как на ПК. */
  placePhone: sectionPlaceSchema.optional(),
  decor: z
    .array(sectionDecorSchema)
    .max(SITE_LIMITS.sectionDecor, `Рисунков у секции — не больше ${SITE_LIMITS.sectionDecor}`),
  space: sectionSpaceSchema,
  /** Где показывать секцию (true — показывать). */
  visibility: deviceFlags,
});
export type SectionLook = z.infer<typeof sectionLookSchema>;

// ── Payload целиком ──

const sectionPayloadShape = z.strictObject({
  header: sectionHeaderSchema.optional(),
  columns: sectionColumnsSchema,
  look: sectionLookSchema,
});

/** Payload без перекрёстных проверок (используется при починке по частям). */
export const sectionPayloadShapeSchema = sectionPayloadShape;

type SectionPayloadShape = z.infer<typeof sectionPayloadShape>;

/**
 * Перекрёстные правила, которые не выразить полем: доли = число колонок и
 * сумма 12, порядок на телефоне — перестановка 1..N (v3: для 1–4 колонок,
 * прежний запрет «только у двух» снят), наезд/слияние, привязка рисунков,
 * уникальные id, обязательные поля в зависимости от источника данных.
 * Не проверяются (решает витрина, db-design-sb120-stage1 §4): «По фото» без
 * фото и положение при высоте «по содержимому» — рисуется «по содержимому».
 */
export function checkSectionPayload(p: SectionPayloadShape, ctx: z.RefinementCtx): void {
  const count = p.columns.items.length;
  const { shares, phoneOrder } = p.columns;
  if (shares.length !== count) {
    ctx.addIssue({
      code: "custom",
      path: ["columns", "shares"],
      message: `Долей колонок ${shares.length}, а колонок ${count}`,
    });
  } else if (!isValidShares(shares, count)) {
    ctx.addIssue({
      code: "custom",
      path: ["columns", "shares"],
      message: "Доли колонок в сумме должны давать 12",
    });
  }
  if (phoneOrder !== undefined && !isValidPhoneOrder(phoneOrder, count)) {
    ctx.addIssue({
      code: "custom",
      path: ["columns", "phoneOrder"],
      message: "Порядок на телефоне — каждая колонка ровно один раз",
    });
  }
  if (p.look.space.overlap > 0 && p.look.space.mergeWithPrev) {
    ctx.addIssue({
      code: "custom",
      path: ["look", "space", "mergeWithPrev"],
      message: "«Наезд на блок выше» и «Слить с блоком выше» вместе нельзя",
    });
  }
  p.look.decor.forEach((decor, index) => {
    if (decor.column !== null && decor.column > count) {
      ctx.addIssue({
        code: "custom",
        path: ["look", "decor", index, "column"],
        message: `Рисунок привязан к колонке ${decor.column}, а колонок ${count}`,
      });
    }
  });

  const seen = new Set<string>();
  p.columns.items.forEach((column, col) => {
    column.elements.forEach((element, row) => {
      const path = ["columns", "items", col, "elements", row];
      if (seen.has(element.id)) {
        ctx.addIssue({ code: "custom", path: [...path, "id"], message: `Повторяется id элемента «${element.id}»` });
      }
      seen.add(element.id);
      for (const issue of elementCrossIssues(element)) {
        ctx.addIssue({ code: "custom", path: [...path, ...issue.path], message: issue.message });
      }
    });
  });
}

/** Повторы id внутри списка элемента. */
function duplicateIds(
  list: readonly { id: string }[],
  path: (string | number)[],
  what: string,
): { path: (string | number)[]; message: string }[] {
  const seen = new Set<string>();
  const issues: { path: (string | number)[]; message: string }[] = [];
  list.forEach((item, index) => {
    if (seen.has(item.id)) issues.push({ path: [...path, index, "id"], message: `Повторяется id ${what} «${item.id}»` });
    seen.add(item.id);
  });
  return issues;
}

/** Скрыт ли элемент хотя бы на одном устройстве (`hide` с true). */
export function isElementHiddenSomewhere(element: Pick<SectionElement, "hide">): boolean {
  return element.hide !== undefined && (element.hide.phone || element.hide.tablet || element.hide.desktop);
}

/** Правила элемента «поле зависит от поля». */
function elementCrossIssues(element: SectionElement): { path: (string | number)[]; message: string }[] {
  // SB34: обязательный элемент шаблона товара виден на всех устройствах.
  if (isProductRequiredKind(element.kind) && isElementHiddenSomewhere(element)) {
    return [{ path: ["hide"], message: "Обязательный элемент виден на всех устройствах — скрыть его нельзя" }];
  }
  switch (element.kind) {
    case "products":
      if (element.source === "badge" && element.badge === null) {
        return [{ path: ["badge"], message: "Выберите бейдж" }];
      }
      if (element.source === "manual" && element.ids.length === 0) {
        return [{ path: ["ids"], message: "Добавьте хотя бы один товар" }];
      }
      return [];
    case "categories":
      return element.source === "manual" && element.ids.length === 0
        ? [{ path: ["ids"], message: "Выберите хотя бы одну категорию" }]
        : [];
    case "articles":
      return element.source === "manual" && element.ids.length === 0
        ? [{ path: ["ids"], message: "Выберите хотя бы одну статью" }]
        : [];
    case "reviews":
      return element.pick === "manual" && element.ids.length === 0
        ? [{ path: ["ids"], message: "Выберите хотя бы один отзыв" }]
        : [];
    case "contacts":
      return new Set(element.kinds).size === element.kinds.length
        ? []
        : [{ path: ["kinds"], message: "Контакт отмечен дважды" }];
    case "stats":
      return duplicateIds(element.items, ["items"], "показателя");
    case "table": {
      const issues = [
        ...duplicateIds(element.columns, ["columns"], "столбца"),
        ...duplicateIds(element.rows, ["rows"], "строки"),
      ];
      element.rows.forEach((row, index) => {
        if (row.cells.length !== element.columns.length) {
          issues.push({
            path: ["rows", index, "cells"],
            message: `В строке должно быть ${element.columns.length} ячеек — по числу столбцов`,
          });
        }
      });
      return issues;
    }
    case "announcements": {
      const seen = new Set<string>();
      const issues: { path: (string | number)[]; message: string }[] = [];
      if (element.source === "manual" && element.items.length === 0) {
        issues.push({ path: ["items"], message: "Нужно хотя бы одно объявление" });
      }
      element.items.forEach((item, index) => {
        if (seen.has(item.id)) issues.push({ path: ["items", index, "id"], message: `Повторяется id объявления «${item.id}»` });
        seen.add(item.id);
      });
      return issues;
    }
    default:
      return [];
  }
}

export const sectionPayloadSchema = sectionPayloadShape.superRefine(checkSectionPayload);

export type SectionPayload = z.infer<typeof sectionPayloadSchema>;
